import { BaseTag, Space } from '@/components';
import { useCommentList } from '@/features/comment/hooks/comment';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import { useProject } from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { CommentOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash-es';
import type { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import type { PinpointMappingType } from '../../models/pinpoint';
import { generatePinpointKey } from '../../utils/generate-pinpoint-key';
import { isNotUsePinpoint } from '../../utils/pinpoint';
import { usePinpoint } from './PinpointProvider';

type Props = {
  pinpoint?: PinpointMappingType;
  filteredOrganizationFiscalYear: number | undefined;
};

export const PinpointMarkerForTableCell: FC<PropsWithChildren<Props>> = (
  props,
) => {
  const { id } = useId();
  const { data: project } = useProject(id);
  const { notUsePinpoint } = isNotUsePinpoint(project);

  if (notUsePinpoint || !props.pinpoint) {
    return props.children;
  }

  return <BasePinpointMarkerForTableCell {...props} />;
};

const BasePinpointMarkerForTableCell: FC<PropsWithChildren<Props>> = ({
  children,
  pinpoint,
  filteredOrganizationFiscalYear,
}) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { getPinpointScrollTargetRef } = usePinpoint();
  const commentStackSiderView = useCommentStackSiderView();
  const { data: project } = useProject(id);
  const { notUsePinpoint } = isNotUsePinpoint(project);
  const commentList = useCommentList({
    params: { pinpointKey: generatePinpointKey(pinpoint?.field) },
    select: (d) => d.results,
    enabled:
      !!pinpoint && commentStackSiderView.list.length > 0 && !notUsePinpoint,
  });
  const count = commentList.data?.filter((comment) => {
    return isEqual(comment.pinpoint, pinpoint);
  }).length;

  const showPinpointTag = commentStackSiderView.list.length > 0 && count;

  if (!showPinpointTag) {
    return children;
  }

  return (
    <Space>
      <div ref={getPinpointScrollTargetRef(pinpoint)} />
      {children}
      <BaseTag
        color="green"
        icon={<CommentOutlined />}
        onClick={() => {
          commentStackSiderView.set({
            payload: [
              {
                config: {
                  viewType: 'comment-list',
                  target: {
                    projectFiscalYear: filteredOrganizationFiscalYear,
                  },
                  pinpointKey: generatePinpointKey(pinpoint?.field),
                  pinpoint: pinpoint,
                },
                siderType: 'comment',
              },
            ],
            target: {
              type: 'project',
              projectId: id,
            },
          });
        }}
        style={{ cursor: 'pointer' }}
      >
        {`${count}${t('comment')}`}
      </BaseTag>
    </Space>
  );
};
